import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from '../../redux/store';
import { setFilters } from '../../redux/slices/resourcing';
import styles from './ResourcingFilters.module.css';
import DatePicker from '../DatePicker/DatePicker';
import Dropdown from '../DropDownBox/Dropdown';

function ResourcingFilters({ onFiltersChange, compact = false }) {
    const dispatch = useDispatch();
    const { filters, teams, isTeamsLoading } = useSelector((state) => state.resourcing);

    const [localFilters, setLocalFilters] = useState({
        startDate: filters.startDate || '',
        endDate: filters.endDate || '',
        teamFilter: filters.teamFilter || 'NZ'
    });

    // Set default dates on component mount (4 weeks only)
    useEffect(() => {
        if (!localFilters.startDate || !localFilters.endDate) {
            const today = new Date();
            const monday = new Date(today);
            monday.setDate(today.getDate() - today.getDay() + 1); // Get Monday of current week

            const endDate = new Date(monday);
            endDate.setDate(monday.getDate() + (4 * 7) - 1); // 4 weeks from Monday

            const newFilters = {
                startDate: monday.toISOString().split('T')[0],
                endDate: endDate.toISOString().split('T')[0],
                teamFilter: localFilters.teamFilter
            };

            setLocalFilters(newFilters);
            dispatch(setFilters(newFilters));
            onFiltersChange?.(newFilters);
        }
    }, []);

    const handleFilterChange = (key, value) => {
        const newFilters = { ...localFilters, [key]: value };
        setLocalFilters(newFilters);
        dispatch(setFilters(newFilters));
        onFiltersChange?.(newFilters);
    };

    // Handle date change for 4-week range
    const handleDateRangeChange = (dateRange) => {
        const startDate = new Date(dateRange.startDate);

        // If it's not a Monday, find the previous Monday
        const mondayStart = new Date(startDate);
        const dayOfWeek = mondayStart.getDay();
        const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Sunday = 0, so subtract 6 to get to Monday
        mondayStart.setDate(mondayStart.getDate() - daysToSubtract);

        // Calculate end date (4 weeks - 1 day from start)
        const endDate = new Date(mondayStart);
        endDate.setDate(mondayStart.getDate() + (4 * 7) - 1);

        const newFilters = {
            ...localFilters,
            startDate: mondayStart.toISOString().split('T')[0],
            endDate: endDate.toISOString().split('T')[0]
        };

        setLocalFilters(newFilters);
        dispatch(setFilters(newFilters));
        onFiltersChange?.(newFilters);
    };

    // Prepare team options for dropdown
    const teamOptions = [
        'NZ Team (Default)',
        'All Teams',
        ...teams.map(team => team.label)
    ];

    const handleTeamChange = (selectedTeam) => {
        let teamValue;
        if (selectedTeam === 'NZ Team (Default)') {
            teamValue = 'NZ';
        } else if (selectedTeam === 'All Teams') {
            teamValue = 'all';
        } else {
            teamValue = teams.find(team => team.label === selectedTeam)?.value || selectedTeam;
        }
        handleFilterChange('teamFilter', teamValue);
    };

    // Get current selected team for dropdown
    const getCurrentTeamSelection = () => {
        if (localFilters.teamFilter === 'NZ') return 'NZ Team (Default)';
        if (localFilters.teamFilter === 'all') return 'All Teams';
        const team = teams.find(team => team.value === localFilters.teamFilter);
        return team ? team.label : localFilters.teamFilter;
    };

    // Get the initial date for the DatePicker
    const getInitialDateForRangePicker = () => {
        if (localFilters.startDate) {
            return new Date(localFilters.startDate);
        }
        return new Date();
    };

    // Format date range display
    const formatDateRange = () => {
        if (localFilters.startDate && localFilters.endDate) {
            const start = new Date(localFilters.startDate);
            const end = new Date(localFilters.endDate);
            return `${start.toLocaleDateString('en-GB')} - ${end.toLocaleDateString('en-GB')}`;
        }
        return 'Not selected';
    };

    if (compact) {
        return (
            <div className={styles.inlineFilters}>
                <div className={`${styles.inlineFilterGroup} ${styles.inlineTeamWrapper}`}>
                    <label className={styles.filterLabel}>Team</label>
                    <Dropdown
                        options={teamOptions}
                        initialSelectedValue={getCurrentTeamSelection()}
                        onValueChange={handleTeamChange}
                        disabled={isTeamsLoading}
                        placeholder="Select Team"
                        options_width={'280px'}
                        className={styles.inlineTeamSelect}
                    />
                </div>
                <div className={styles.inlineFilterGroup}>
                    <label className={styles.filterLabel}>4-Week Date Range</label>
                    <div className={styles.inlineDatePickerWrapper}>
                        <DatePicker
                            key={`${localFilters.startDate}-${localFilters.endDate}`}
                            initialDate={getInitialDateForRangePicker()}
                            onDatesChange={handleDateRangeChange}
                            mondayOnly={false}
                            visableIcon={true}
                        />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.filtersContainer}>
            <div className={styles.filtersHeader}>
                <h3 className={styles.filtersTitle}>Filters</h3>
            </div>

            <div className={styles.filtersGrid}>
                {/* Team Filter */}
                <div className={styles.filterGroup}>
                    <label className={styles.filterLabel}>Team</label>
                    <Dropdown
                        options={teamOptions}
                        initialSelectedValue={getCurrentTeamSelection()}
                        onValueChange={handleTeamChange}
                        disabled={isTeamsLoading}
                        placeholder="Select Team"
                    />
                </div>

                {/* Date Range Picker */}
                <div className={styles.filterGroup}>
                    <label className={styles.filterLabel}>4-Week Date Range</label>
                    <div className={styles.datePickerWrapper}>
                        <DatePicker
                            key={`${localFilters.startDate}-${localFilters.endDate}`}
                            initialDate={getInitialDateForRangePicker()}
                            onDatesChange={handleDateRangeChange}
                            mondayOnly={false}
                            visableIcon={true}
                        />
                        {/* Omit the date range row below the picker */}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ResourcingFilters; 